/**
 * React Query hooks for Customer Config API (COMP-002).
 * IP-002: GET /api/customers, GET /api/customers/:tenantId
 * Admin cache: GET /api/admin/cache/stats, POST /api/admin/cache/refresh
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/providers/api-provider";
import type { CustomerGDAPConfig, PaginatedResponse } from "@/types";

interface CustomerListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  tier?: string;
  strategy?: string;
}

interface CacheStats {
  cachedEntries: number;
  lastRefreshTime: string;
}

interface CacheRefreshResult {
  refreshed: number;
  duration: number;
}

export function useCustomerList(params: CustomerListParams = {}) {
  const api = useApiClient();
  const { page = 1, pageSize = 25, search, tier, strategy } = params;

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("pageSize", String(pageSize));
  if (search) queryParams.set("search", search);
  if (tier) queryParams.set("tier", tier);
  if (strategy) queryParams.set("strategy", strategy);

  return useQuery({
    queryKey: ["customers", { page, pageSize, search, tier, strategy }],
    queryFn: () =>
      api.get<PaginatedResponse<CustomerGDAPConfig>>(
        `/api/customers?${queryParams.toString()}`,
      ),
    staleTime: 60_000,
  });
}

export function useCustomerDetail(tenantId: string | undefined) {
  const api = useApiClient();

  return useQuery({
    queryKey: ["customers", tenantId],
    queryFn: () =>
      api.get<CustomerGDAPConfig>(`/api/customers/${tenantId}`),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

export function useCacheStats() {
  const api = useApiClient();

  return useQuery({
    queryKey: ["cache", "stats"],
    queryFn: () => api.get<CacheStats>("/api/admin/cache/stats"),
    staleTime: 60_000,
  });
}

export function useCacheRefresh() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<CacheRefreshResult>("/api/admin/cache/refresh", {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      void queryClient.invalidateQueries({ queryKey: ["cache", "stats"] });
    },
  });
}
